/*
 * Copyright (c) Nexign, JSC, 2019-2021.
 */
/**
 * Modal component to handle footer errors
 *
 * @author Anton Panasenko
 * @author Yakov Gushchin
 * @author Aleksandr Perkhunov
 * @version 1.11.0
 * @since 1.0.0
 */

import { connect, useSelector } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import { FeatureNameEnum } from '@/main/modules/role/constants/guard/guardConstants';
import { makeSelectFeatureEnabled } from '@/main/pages/PageAuth/selectors/authSelectors';
import { openRoute } from '@/main/routes/constants/externalRoutes';
import { RoutePath } from '@/main/routes/constants/routesConstants';
import { Button, TextBlock } from '@components/common';

import { makeSelectTranslationCommon } from '../../../../modules/internationalization/intSelectors';
import { ICommonTranslation } from '../../../../modules/internationalization/types';
import { IRootReducer } from '../../../../reducers/rootReducer';
import { getIsFastActionPage } from '../../utils/tableUtils';
import { FooterModalStyled, FooterModalTextStyled, FooterModalTitleStyled } from './styled/footerModalStyled';

interface IConnectedProps {
    translation: ICommonTranslation;
}

export interface IFooterErrorModalProps {
    message: string;
    onCloseModal: () => void;
    title?: string;
}

export type TFooterErrorModal = IFooterErrorModalProps & IConnectedProps;

const FooterSuccessModal = (props: TFooterErrorModal) => {
    const { message, onCloseModal, title, translation } = props;

    const isMainOperationsEnabled = useSelector(makeSelectFeatureEnabled(FeatureNameEnum.MAIN_OPERATIONS));

    const isFastActionPage = getIsFastActionPage();

    const onOkHandler = () => {
        if (isFastActionPage) {
            openRoute(RoutePath.dashboard)
        }
        onCloseModal();
    };

    return (
        <FooterModalStyled
            data-component={'modal-popup'}
            data-name={'footer-success-modal'}>
            <FooterModalTitleStyled className="mb-3">
                {title || translation.modal.success}
            </FooterModalTitleStyled>
            <FooterModalTextStyled className="mb-5">
                <TextBlock>{message}</TextBlock>
            </FooterModalTextStyled>
            {
                isMainOperationsEnabled && 
                <Button onClick={onOkHandler}>
                    {translation.modal.good}
                </Button>
            }
        </FooterModalStyled>
    );
};

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    translation: makeSelectTranslationCommon(),
});

export default connect(mapStateToProps)(FooterSuccessModal);
